import { OpenAPIClientAxios } from "openapi-client-axios";
import axiosRetry from "axios-retry";
import definition from "@/assets/docs/api.json";
import { Client } from "@/assets/docs/client.d";
import { isEntraAuthActive, getActiveAccount, acquireAccessToken, loginInteractive } from "@/auth/entraAuthService";

export default {
  install: app => {
    const api = new OpenAPIClientAxios({
      definition: definition,
      axiosConfigDefaults: {
        // Matches the container-app startup probe budget (30 x 5s = 150s, see GEN-7657).
        // A cold start the platform still considers healthy must not time out client-side.
        timeout: 150 * 1000,
        withCredentials: true, // Check cross-site Access-Control
      },
    });

    const client = api.initSync<Client>();
    client.defaults.headers.common["Content-Type"] = "application/json";

    /*
     * LS
     *
     * When the Entra External ID flow is active, every request carries a bearer
     * token acquired silently from MSAL. When it can't be acquired (no signed-in
     * account yet) we send the request without one; app-web answers 401 and the
     * response interceptor below escalates to an interactive sign-in. When the
     * flow is off this is a no-op and the legacy cookie (withCredentials) flow
     * stays in force.
     */
    client.interceptors.request.use(async config => {
      if (isEntraAuthActive()) {
        const token = await acquireAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });

    client.interceptors.response.use(
      response => response,
      function (error) {
        /*
         * LS
         * 
         * HTTP response 498 Invalid Token is an unofficial client error that is specific to ArcGIS
         * and indicates that the HTTP request included a token, but it was not accepted.
         * See https://http.dev/498
         * 
         * We, GenTwo, use it to instruct the frontend application to redirect to ADB2C.
         * This is because, by-design, frontend will get a CORS error from ADB2C
         * when trying to redirect. So, we need to manually navigate the frontend to ADB2C.
         */
        if(error.status === 498) {
          /*
           * LS
           *
           * 498 hands back a cookie/ADB2C re-challenge URL (function switch,
           * impersonation). That mechanism is cookie-only and has no bearer
           * equivalent yet (deferred to the follow-up ticket), so under the Entra
           * flow we must not navigate to it - that would drop the user into a dead
           * ADB2C redirect. Fail safe by rejecting so the caller surfaces an error.
           */
          if(isEntraAuthActive()) {
            return Promise.reject(error);
          }

          window.location.href = error.response.data;
          // Halt the promise chain while the browser navigates away,
          // so callers never receive the error body as response data.
          return new Promise(() => {});
        }

        /*
         * LS
         *
         * By design, if we get HTTP response 401 Unauthorized
         * this means that we want to refresh the cookies or force a re-login
         */
        if(error.status === 401) {
          /*
           * LS
           *
           * Under the Entra flow, a 401 with a signed-in account is an
           * authorization failure (the token was accepted but app-web did not
           * resolve it to a user, e.g. not provisioned) - re-authenticating would
           * just loop, so we surface it and let the app land on access-denied.
           * With no account it is a genuine authentication need: sign in, keeping
           * the current path as state so we can return to it afterwards.
           */
          if(isEntraAuthActive()) {
            if(getActiveAccount()) {
              return Promise.reject(error);
            }

            loginInteractive({ state: window.location.pathname });
            return new Promise(() => {});
          }

          let returnUrl = '/';
          if(error.request?.responseURL) {
            returnUrl = new URL(error.request.responseURL).pathname;
          }

          window.location.href = `/apptwo/account/login?return_url=${returnUrl}`;
          // Halt the promise chain while the browser navigates away,
          // so callers never receive the error body as response data.
          return new Promise(() => {});
        }
        
        return Promise.reject(error);
    });
   
    axiosRetry(client, {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay,
    });
    
    app.config.globalProperties.$apiClient = client;
  },
};
